import { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Link } from 'react-router';
import cn from 'classnames';

import { VALID_EMAIL } from 'common/regex';
import { toUSD } from 'helpers/NumberFormats';
import { translateCartError } from 'apis/mafia';
import { changeQuantity, fetchCartItems } from 'actions/cart';
import { fetchEGiftCardDesigns, setGiftCardDesign } from 'actions/eGiftCard';
import SiteAwareMetadata from 'components/SiteAwareMetadata';
import MartyLink from 'components/common/MartyLink';
import { Loader } from 'components/Loader';
import MelodyCarousel from 'components/common/MelodyCarousel';
import EGCDetailsForm from 'components/productdetail/EGCDetailsForm';
import marketplace from 'cfg/marketplace.json';
import css from 'styles/containers/eGiftCard.scss';
import { mCard, image as mImage } from 'styles/components/common/melodyCard.scss';

const { shortName, pdp, cart: { cartName } } = marketplace;
const isReady = designs =>!!designs;

export class EGiftCard extends Component {
  static contextTypes = {
    router: PropTypes.object.isRequired,
    testId: PropTypes.func
  }

  static fetchDataOnServer({ dispatch }) {
    return Promise.all([
      dispatch(fetchEGiftCardDesigns()),
      dispatch(fetchCartItems())
    ]);
  }

  state = {
    isEmailValid: true,
    isAmountValid: true,
    isAdding: false,
    price: 0
  };

  componentDidMount() {
    const {
      eGiftCards: { designs },
      cart: { isLoaded: cartIsLoaded },
      fetchEGiftCardDesigns,
      fetchCartItems
    } = this.props;

    if (!designs) {
      fetchEGiftCardDesigns();
    }

    if (!cartIsLoaded) {
      fetchCartItems();
    }
  }

  onAddToCart = e => {
    e.preventDefault();

    const { eGiftCards: { asin }, changeQuantity, fetchCartItems } = this.props;
    const { recipientName, recipientEmail, senderName, message, amount } = e.target;
    this.setState({ isAdding: true });

    changeQuantity({ items: [{
      asin,
      recipientName: recipientName.value,
      recipientEmail: recipientEmail.value,
      senderName: senderName.value,
      message: message.value,
      amount: amount.value,
      itemType: 'egc',
      egc: true,
      quantity: 1
    }] }, { firePixel: true })
      .then(resp=> {
        const error = translateCartError(resp);
        if (error) {
          this.setState({ isAdding: false });
          alert(error);
        } else {
          fetchCartItems(); // TODO: remove this logic once https://github01.zappos.net/mweb/marty/issues/6291 is addressed on the mafia side
          this.context.router.push('/cart');
        }
      });
  }

  onDesignChosen = event => {
    const asin = event.currentTarget.getAttribute('data-asin');

    this.props.setGiftCardDesign(asin);
    this.setState({ isEmailValid: true, isAmountValid: true });
  }

  onPriceChange = event => {
    const price = event.currentTarget.value || 0;
    this.setState({ price });
  }

  onValidateAmount = event => {
    const amount = event.currentTarget.value && Math.ceil(event.currentTarget.value);

    this.setState({
      isAmountValid: (
        !isNaN(amount)
        && amount > 0
        && amount <= 1000)
    });
  }

  onMessageUpdate = e => {
    const { currentTarget: { value: message } } = e;
    this.setState({ message });
  }

  onValidateEmail = event => {
    const email = event.currentTarget.value;
    this.setState({ isEmailValid: VALID_EMAIL.test(email) });
  }

  getDesign = designs => {
    const { eGiftCards: { asin } } = this.props;

    return (
      asin ?
        designs.find((design => design.asin === asin)) :
        designs[0]
    );
  }

  render() {
    const { eGiftCards: { designs }, cart, isEgcInCart } = this.props;
    const { testId } = this.context;

    // if egc designs are fetched and cart is loaded
    if (!(isReady(designs) && cart.isLoaded)) {
      return <Loader />;
    }

    const { isAmountValid, isAdding, isEmailValid, price, message } = this.state;
    const selectedDesign = this.getDesign(designs);

    if (isEgcInCart) {
      return (
        <div className={cn(css.container, css.egcInCart)} data-test-id={testId('egcInCartMsg')}>
          <b>Oops!</b>
          <p>Only 1 e-gift card may be purchased in a single order. Please checkout and start a new order to purchase another gift card.</p>

          <MartyLink to="/cart" data-test-id={testId('goToCart')}>Go to shopping {cartName}</MartyLink>
        </div>
      );
    }

    return (
      <SiteAwareMetadata>
        <div className={css.container}>
          <h1 data-test-id={testId('heading')}>{shortName} e-Gift Card</h1>

          <div className={css.columns}>
            <div className={css.designs}>
              <div className={css.selected}>

                <img data-test-id={testId('egcImage')} src={selectedDesign.imageUrl} alt="Electronic Gift Card"/>

                <div className={css.info}>
                  <div className={css.code}>
                    <span>Asin</span>
                    <span data-test-id={testId('egcAsin')}>{selectedDesign.asin}</span>
                  </div>

                  <div className={css.amount}>{toUSD(price)}</div>
                </div>
              </div>

              <div className={css.carousel}>
                { designs.length > 1 &&
                  <MelodyCarousel>
                    {designs.map(design => {
                      const { asin, imageUrl, designName } = design;
                      return (
                        <div key={asin} className={cn(css.designContainer, mCard)}>
                          <Link
                            className={mImage}
                            onClick={this.onDesignChosen}
                            data-asin={asin}>
                            <img src={imageUrl} alt={designName}/>
                          </Link>
                        </div>
                      );
                    })}
                  </MelodyCarousel>
                }
              </div>

            </div>

            <EGCDetailsForm
              action={pdp.egcAddToCartAction}
              design={selectedDesign}
              onValidateAmount={this.onValidateAmount}
              onValidateEmail={this.onValidateEmail}
              onPriceChange={this.onPriceChange}
              message={message}
              isAdding={isAdding}
              isEgcInCart={isEgcInCart}
              isEmailValid={isEmailValid}
              isAmountValid={isAmountValid}
              onMessageUpdate={this.onMessageUpdate}
              onAddToCart={this.onAddToCart} />
          </div>

        </div>
      </SiteAwareMetadata>
    );

  }
}

function mapStateToProps(state) {
  const { cart, eGiftCards } = state;
  let isEgcInCart;

  if (cart.cartObj && cart.cartObj.activeItems) {
    isEgcInCart = !!cart.cartObj.activeItems.find(item => item.egc === true);
  }

  return {
    eGiftCards,
    cart,
    isEgcInCart
  };
}

export default connect(mapStateToProps, {
  changeQuantity,
  fetchEGiftCardDesigns,
  fetchCartItems,
  setGiftCardDesign
})(EGiftCard);
